import { consoleLoggerFactory, getDefaultLoggerOptions, type ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

import { goFetch } from "@/utils/network-utils";

import LocalForagePlugin, { sectorHistory, syncState } from "@/plugins/LocalForagePlugin";
import { sectorHistorySyncKey } from "@/types/products/product-definitions";

import { ContentfulSector, PersistedSectorsState, SectorAlert, SectorMarketState } from "@/types/entities";
import { CompositionContext } from "@/services/data/sync-supervisor";
import type { PersistedQueryPayload } from "@/types/api/fetch";
import { DateTime } from "luxon";
import {
    SectorAlertApiResponse,
    SectorHistoryApiResponse,
    SectorHistoryForDate,
    SectorStatsApiResponse
} from "@/types/api/market-data";
import { SectorAlertTypes } from "@/types/enums/sector-alerts";
import { Tuple } from "@/types/adts";

type SectorHistoryMetaApiResponse = {
    sectorHistoryMetaData: {
        dts: string[]
    }
}

async function fetchSectorHistoryKeys(): Promise< string[] > {
    let allSectorHistoryDates: string[] = [];

    // Timing - testing to see how expensive each method is
    const t0: number = performance.now();

    let sectorHistoryMetaPayload: string[] = [];
    const response =
        await goFetch< SectorHistoryMetaApiResponse, PersistedQueryPayload >( {
            endpoint: `${ import.meta.env.VITE_SENTIENT_REL_PATH }`,
            payload: {
                id: 'd271528e71bf59d763917ba4acd14273'
            }
        } );

    if ( response?.data?.sectorHistoryMetaData?.dts ) {
        sectorHistoryMetaPayload = response.data.sectorHistoryMetaData.dts;
        const t1: number = performance.now();
        logger.info( `Fetching ${ sectorHistoryMetaPayload.length } sector history keys took ${ t1 - t0 } ms` );

        allSectorHistoryDates = [ ...sectorHistoryMetaPayload ];
    }

    return allSectorHistoryDates;
}

export class SectorHistorySyncCoordinator {
    readonly #compositionContext: CompositionContext;

    constructor( ctx: CompositionContext ) {
        this.#compositionContext = ctx;
    }

    public async patchRepository( authToken: string ): Promise<boolean> {
        let updated = false;

        try {
            const t0: number = performance.now();

            if ( typeof sectorHistory === 'undefined' || typeof syncState === 'undefined' ) {
                // Wait for the driver to settle
                LocalForagePlugin.initialize();
            }

            await syncState.ready();
            // Check for the next refresh interval
            const syncThresholdInMillis = await syncState.getItem<number>( sectorHistorySyncKey );
            if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() ) {
                const nextSyncThresholdInMillis = DateTime.utc().plus( { minutes: 5 } ).toMillis();
                await syncState.setItem( sectorHistorySyncKey, nextSyncThresholdInMillis );

                // Retrieve the latest collection of simple pages from the content service
                const sectorHistoryDates: string[] = await fetchSectorHistoryKeys();

                if ( 0 === sectorHistoryDates.length ) {
                    const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                    await syncState.setItem( sectorHistorySyncKey, nextSyncThresholdInMillis );
                    return updated;
                }

                // Load items currently in cache
                const datesInCache = new Map<string, PersistedSectorsState>();
                await sectorHistory.ready();
                if ( await sectorHistory.length() > 0 ) {
                    // Iterate over all elements within IDB
                    await sectorHistory.iterate( ( item: PersistedSectorsState, key: string ) => {
                        if ( item?.sectors?.length > 0 && item.sectors[ 0 ].bpi.col.length > 0 ) {
                            datesInCache.set( key, item );
                        }
                        return;
                    } );
                }

                const missingDates: string[] = [];
                sectorHistoryDates.forEach( ( dt: string ) => {
                    if ( !datesInCache.has( dt ) ) {
                        missingDates.push( dt );
                    }
                } );

                const historyItems = await refreshFromServer( authToken, this.#compositionContext, missingDates );
                if ( historyItems.length > 0 ) {
                    historyItems.forEach( ( sectorState: PersistedSectorsState ) => {
                        if ( sectorState.sectors?.length > 0 && sectorState.sectors[ 0 ].bpi.col.length > 0 ) {
                            updated = true;

                            sectorHistory.setItem( sectorState.dt, structuredClone( sectorState ) );
                        }
                    } );

                    const t4: number = performance.now();
                    logger.info( `[ sector-history-sync-coordinator ] Persisting all missing sector history took ${ t4 - t0 } ms` );
                }
            }
        } catch ( err: any ) {
            logger.error( JSON.stringify( err?.message ) );
        }

        return updated;
    }
}

const sectorAlertTypeKeyValues: readonly ( readonly [ number, string ] )[] = [
    [ SectorAlertTypes.rs_signal, 'Relative Strength Signal' ],
    [ SectorAlertTypes.rs_direction, 'Relative Strength Direction' ],
    [ SectorAlertTypes.bpi_signal, 'BPI Signal' ],
    [ SectorAlertTypes.bpi_direction, 'BPI Direction' ],
    [ SectorAlertTypes.bpi_status, 'BPI Status' ],
    [ SectorAlertTypes.wk_30_signal, '%30 Signal' ],
    [ SectorAlertTypes.wk_30_direction, '%30 Direction' ],
    [ SectorAlertTypes.wk_10_signal, '%10 Signal' ],
    [ SectorAlertTypes.wk_10_direction, '%10 Direction' ]
];
export const sectorAlertTypeKeyToDisplayValueMapper: Map<number, string> = new Map( sectorAlertTypeKeyValues );

// NOTE: in its current form, there is no "last updated" date b/c the entire history (back to 2020-01-01) is rebuilt each time it is ingested
// b/c we don't know if II has changed values or not
export async function refreshFromServer( authToken: string, ctx: CompositionContext, missingDates: string[] ): Promise<PersistedSectorsState[]> {
    const entities: PersistedSectorsState[] = [];

    const t0: number = performance.now();
    let page: number = 0;
    const pageSize: number = 40;
    let consecutiveFailures: number = 0;
    const maxConsecutiveFailures: number = 2; // Stop after 2 consecutive failures to prevent cascading failures

    do {
        let startingIndex = page * pageSize;
        let endingIndex = startingIndex + pageSize;

        const pagedDates = [ ...missingDates.slice( startingIndex, endingIndex ) ];

        if ( 0 === pagedDates.length ) {
            break;
        }

        const response =
            await goFetch< SectorHistoryApiResponse, PersistedQueryPayload >( {
                endpoint: `${ import.meta.env.VITE_SENTIENT_REL_PATH }`,
                payload: {
                    id: '2c4d19e7c39849b0df82a0143148596c',
                    variables: {
                        skip: 0,
                        limit: pageSize,
                        marketDataFilter: {
                            dates: pagedDates
                        }
                    }
                },
                authToken: authToken
            } );

        // If no response (API failed), increment failure counter and check if we should stop
        if (!response) {
            consecutiveFailures++;
            logger.warn(`Sector history fetch failed (${consecutiveFailures}/${maxConsecutiveFailures} consecutive failures)`);

            if (consecutiveFailures >= maxConsecutiveFailures) {
                logger.warn('Too many consecutive failures, stopping sector history pagination to prevent cascading failures');
                break;
            }

            // Continue to next page to see if it's a transient issue
            page++;
            continue;
        }

        // Reset failure counter on success
        consecutiveFailures = 0;

        const historyItems = response?.data;
        if ( ( historyItems?.sectorHistoryForDates?.items?.length ?? 0 ) > 0 ) {
            let sectorEntities: SectorMarketState[] = [];
            historyItems.sectorHistoryForDates.items
                .forEach( ( i: SectorHistoryForDate ) => {

                    const dateOfAnalysis: string = i.dt;
                    i.history.forEach( ( h: SectorStatsApiResponse ) => {
                        const performers: TopAndBottomPerformers = {
                            top_etfs: [],
                            top_stocks: [],
                            bot_etfs: [],
                            bot_stocks: [],
                        };

                        const alerts: SectorAlert[] = h.alerts?.kind.length > 0 ?
                            [ ...( h.alerts.kind.map( ( kind: number ) =>
                                ( {
                                    id: '',
                                    dt: i.dt,
                                    sector_id: -1,
                                    typeId: kind,
                                    typeName: sectorAlertTypeKeyToDisplayValueMapper.get( kind )!,
                                    bullish: false,
                                    idx: {
                                        val: -1,
                                        col: '',
                                        sig: '',
                                        col_chg_dt: '',
                                        sig_chg_dt: ''
                                    },
                                    bpi: {
                                        val: -1,
                                        col: '',
                                        sig: '',
                                        stat: '',
                                        col_chg_dt: '',
                                        sig_chg_dt: '',
                                        stat_chg_dt: ''
                                    },
                                    p30: {
                                        val: -1,
                                        col: '',
                                        sig: '',
                                        col_chg_dt: '',
                                        sig_chg_dt: ''
                                    },
                                    p10: {
                                        val: -1,
                                        col: '',
                                        sig: '',
                                        col_chg_dt: '',
                                        sig_chg_dt: ''
                                    },
                                    rs: {
                                        rnk: -1,
                                        val: -1,
                                        col: '',
                                        sig: '',
                                        col_chg_dt: '',
                                        sig_chg_dt: ''
                                    },
                                    stocks: [],
                                    etfs: []
                                } )
                            ) ) ]
                            : [];

                        if ( h.alerts?.kind.length > 0 ) {
                            const sectorPerformers: SectorAlertApiResponse = h.alerts;

                            // Mapping the top stocks
                            sectorPerformers.top_stock_1 ? performers.top_stocks.push( sectorPerformers.top_stock_1 ) : '';
                            sectorPerformers.top_stock_2 ? performers.top_stocks.push( sectorPerformers.top_stock_2 ) : '';
                            sectorPerformers.top_stock_3 ? performers.top_stocks.push( sectorPerformers.top_stock_3 ) : '';

                            // Mapping the top etfs
                            sectorPerformers.top_etf_1 ? performers.top_etfs.push( sectorPerformers.top_etf_1 ) : '';
                            sectorPerformers.top_etf_2 ? performers.top_etfs.push( sectorPerformers.top_etf_2 ) : '';
                            sectorPerformers.top_etf_3 ? performers.top_etfs.push( sectorPerformers.top_etf_3 ) : '';

                            // Mapping the bottom stocks
                            sectorPerformers.bot_stock_1 ? performers.bot_stocks.push( sectorPerformers.bot_stock_1 ) : '';
                            sectorPerformers.bot_stock_2 ? performers.bot_stocks.push( sectorPerformers.bot_stock_2 ) : '';
                            sectorPerformers.bot_stock_3 ? performers.bot_stocks.push( sectorPerformers.bot_stock_3 ) : '';

                            // Mapping the top etfs
                            sectorPerformers.bot_etf_1 ? performers.bot_etfs.push( sectorPerformers.bot_etf_1 ) : '';
                            sectorPerformers.bot_etf_2 ? performers.bot_etfs.push( sectorPerformers.bot_etf_2 ) : '';
                            sectorPerformers.bot_etf_3 ? performers.bot_etfs.push( sectorPerformers.bot_etf_3 ) : '';
                        }

                        const sector: ContentfulSector | undefined = ctx.getSectorByTmiId( h.sector_id );
                        if ( typeof sector !== 'undefined' ) {
                            const sectorState: SectorMarketState = {
                                dt: dateOfAnalysis,
                                sector: sector,
                                idx: {
                                    val: h.idx_val,
                                    delta: h.idx_delta,
                                    col: h.idx_col.toLowerCase() === 'x' ? 'O' : 'X',
                                    col_chg_dt: h.dt_last_idx_col,
                                    sig: h.idx_sig?.toLowerCase() === 'buy' ? 'Buy' : ( h.idx_sig?.toLowerCase() === 'sell' ? 'Sell' : '---' ),
                                    sig_chg_dt: h.dt_last_idx_sig
                                },
                                bpi: {
                                    val: h.bpi_val,
                                    col: h.bpi_col.toLowerCase() === 'o' ? 'O' : 'X',
                                    col_chg_dt: h.dt_last_bpi_col,
                                    sig: h.bpi_sig?.toLowerCase() === 'buy' ? 'Buy' : ( h.bpi_sig?.toLowerCase() === 'sell' ? 'Sell' : '---' ),
                                    sig_chg_dt: h.dt_last_bpi_sig,
                                    stat: null,
                                    stat_chg_dt: ''
                                },
                                p30: {
                                    val: h.p30_val,
                                    col: h.p30_col.toLowerCase() === 'o' ? 'O' : 'X',
                                    col_chg_dt: h.dt_last_p30_col,
                                    sig: h.p30_sig?.toLowerCase() === 'buy' ? 'Buy' : ( h.p30_sig?.toLowerCase() === 'sell' ? 'Sell' : '---' ),
                                    sig_chg_dt: h.dt_last_p30_sig
                                },
                                p10: {
                                    val: h.p10_val,
                                    col: h.p10_col?.toLowerCase() === 'o' ? 'O' : 'X',
                                    col_chg_dt: h.dt_last_p10_col,
                                    sig: h.p10_sig?.toLowerCase() === 'buy' ? 'Buy' : ( h.p10_sig?.toLowerCase() === 'sell' ? 'Sell' : '---' ),
                                    sig_chg_dt: ''
                                },
                                rs: {
                                    rnk: h.rs_rank,
                                    val: h.rs_val,
                                    col: h.rs_col.toLowerCase() === 'o' ? 'O' : 'X',
                                    col_chg_dt: h.rs_chg_dt,
                                    sig: h.rs_sig?.toLowerCase() === 'buy' ? 'Buy' : ( h.rs_sig?.toLowerCase() === 'sell' ? 'Sell' : '---' ),
                                    sig_chg_dt: h.dt_last_rs_sig
                                },
                                alerts: []
                            };

                            if ( alerts.length > 0 ) {
                                sectorState.alerts = [ ...composeSectorAlertsForDate( sectorState, alerts, performers ) ];
                            }

                            sectorEntities.push( sectorState );
                        }
                    } );

                    entities.push( {
                        dt: dateOfAnalysis,
                        sectors: [ ...sectorEntities ]
                    } );

                    // Clear the list of sector data
                    sectorEntities = [];
                } );

            const t1: number = performance.now();
            logger.info( `Fetching & mapping sector market data took ${ t1 - t0 } ms` );
        }

        // update the page #
        page++;
    } while ( page * pageSize < missingDates.length );

    calculateMissingIdxDeltas( entities );

    return entities;
}

/**
 * Fills in missing EquallyWeightedIndex.delta values by computing the difference
 * between the current idx.val and the previous date's idx.val for each sector.
 * Mutates the data in place. Sorts chronologically to ensure correct ordering.
 */
export function calculateMissingIdxDeltas( data: PersistedSectorsState[] ): void {
    if ( data.length < 2 ) return;

    data.sort( ( a, b ) => a.dt.localeCompare( b.dt ) );

    const previousIdxValBySectorId = new Map< number, number >();

    for ( const state of data ) {
        for ( const sectorState of state.sectors ) {
            if ( !sectorState.idx ) continue;

            const sectorId = sectorState.sector.id;
            const previousVal = previousIdxValBySectorId.get( sectorId );

            if ( sectorState.idx.delta == null && previousVal != null ) {
                sectorState.idx.delta = sectorState.idx.val - previousVal;
            }

            previousIdxValBySectorId.set( sectorId, sectorState.idx.val );
        }
    }
}

export function hydrate( entity: PersistedSectorsState, ctx: CompositionContext ): Tuple<string, Map<number, SectorMarketState>> {
    const dateOfAnalysis: string = entity.dt;

    const sectorToStateMap = new Map<number, SectorMarketState>();
    for ( const sectorState of entity.sectors ) {
        const sector = ctx.getSectorByTmiId( sectorState.sector.id );
        if ( typeof sector !== 'undefined' ) {
            sectorToStateMap.set( sectorState.sector.id, {
                dt: dateOfAnalysis,
                sector: sector,
                idx: sectorState.idx,
                bpi: sectorState.bpi,
                p30: sectorState.p30,
                p10: sectorState.p10,
                rs: sectorState.rs,
                alerts: sectorState.alerts
            } );
        } else {
            logger.warn( `The given TMI ID (${ sectorState.sector.id }) did not map to a valid sector object.` );
        }
    }

    return {
        item1: dateOfAnalysis,
        item2: sectorToStateMap
    }
}

type TopAndBottomPerformers = {
    top_stocks: string[],
    bot_stocks: string[],
    top_etfs: string[],
    bot_etfs: string[]
}

export function composeSectorAlertsForDate( eodState: Readonly< SectorMarketState >, alerts: SectorAlert[], performers: TopAndBottomPerformers ): Readonly<SectorAlert>[] {
    const workingSectorAlertList: Readonly< SectorAlert >[] = [];
    for ( const alert of alerts ) {
        const alertKey = `${ alert.dt }+${ eodState.sector.id }+${ alert.typeId }`;

        let bullish: boolean = false;
        switch ( alert.typeId ) {
            case SectorAlertTypes.rs_signal:
                bullish = isBullishSignal( eodState.rs.sig?.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.rs_direction:
                bullish = isBullishDirection( eodState.rs.col.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.bpi_signal:
                bullish = isBullishSignal( eodState.bpi.sig?.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.bpi_direction:
                bullish = isBullishDirection( eodState.bpi.col.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.bpi_status:
                bullish = isBullishStatus( eodState.bpi.stat?.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.wk_30_signal:
                bullish = isBullishSignal( eodState.p30.sig?.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.wk_30_direction:
                bullish = isBullishDirection( eodState.p30.col.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.wk_10_signal:
                bullish = isBullishSignal( eodState.p10.sig?.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.wk_10_direction:
                bullish = isBullishDirection( eodState.p10.col.toLowerCase() ?? '' );
                break;
        }

        // Build the Sector Alert
        workingSectorAlertList.push( {
            id: alertKey,
            dt: alert.dt,
            sector_id: eodState.sector.id,
            typeId: alert.typeId,
            typeName: alert.typeName,
            bullish: bullish,
            bpi: eodState.bpi,
            p30: eodState.p30,
            p10: eodState.p10,
            rs: eodState.rs,
            stocks: bullish ? performers.top_stocks : performers.bot_stocks,
            etfs: bullish ? performers.top_etfs : performers.bot_etfs
        } );
    }

    return workingSectorAlertList;
}

export function isBullishSignal( signal: string ): boolean {
    switch ( signal ) {
        case 'buy':
            return true;
        case 'sell':
        default:
            return false
    }
}

export function isBullishDirection( direction: string ): boolean {
    switch ( direction ) {
        case 'x':
            return true;
        case 'o':
        default:
            return false
    }
}

function isBullishStatus( status: string ): boolean {
    switch ( status ) {
        case 'bull confirmed':
            return true;
        case 'bull alert':
            return true;
        case 'bear correction':
            return true;
        case 'bear confirmed':
            return false;
        case 'bull correction':
            return false;
        case 'bull top':
            return false;
        case 'bear alert':
            return false;
        default:
            return false;
    }
}